import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from '../utils/translations';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAllProperties, selectFeaturedProperties } from '../store/selectors/propertySelectors';
import { PROPERTY_TYPE_OPTIONS } from '../constants/propertyTypes';
import { agentsAPI } from '../services/api';
import { formatInstagramDisplay, getInstagramUrl } from '../utils/instagramHelpers';
import { getPhoneUrl, getEmailUrl } from '../utils/contactHelpers';
import { getAgentPropertyCount } from '../utils/agentHelpers';
import { getListingStatusLabel, resolveListingStatus } from '../constants/listingStatus';
import { resolveMediaUrl } from '../utils/mediaUrl';
import InstagramIcon from '../components/InstagramIcon/InstagramIcon';
import AgentPhoto from '../components/AgentPhoto/AgentPhoto';
import { PageLoader } from '../components/loading';
import './AgentsPage.css';

const AgentsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const featuredProperties = useSelector(selectFeaturedProperties);
  const allProperties = useSelector(selectAllProperties);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch agents from API
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await agentsAPI.getAll();
        // Ensure data is an array
        if (Array.isArray(data)) {
          setAgents(data);
        } else {
          console.warn('Agents API returned non-array data:', data);
          setAgents([]);
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
        // Don't show error if it's just an empty array
        if (err.message && !err.message.includes('empty')) {
          setError(err.message || 'Failed to load agents');
        }
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  const categories = useMemo(() => {
    const counts = {};

    allProperties.forEach((property) => {
      const type = property.propertyType;
      if (!type) return;
      counts[type] = (counts[type] || 0) + 1;
    });

    return PROPERTY_TYPE_OPTIONS
      .filter((type) => counts[type] > 0)
      .map((type) => ({
        type,
        label: t(`propertyTypes.${type}`) || type,
        count: counts[type],
      }));
  }, [allProperties, t]);

  const recentlyViewed = featuredProperties.slice(0, 3);

  if (loading) {
    return (
      <div className="agents-page">
        <div className="container">
          <div className="agents-header">
            <h1>{t('agents.title') || 'Agjentët'}</h1>
          </div>
          <PageLoader type="agents" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agents-page">
        <div className="container">
          <div className="agents-header">
            <h1>{t('agents.title') || 'Agjentët'}</h1>
          </div>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ 
              background: 'rgba(220, 53, 69, 0.1)', 
              border: '2px solid rgba(220, 53, 69, 0.3)', 
              borderRadius: '8px', 
              padding: '1.5rem',
              color: '#dc3545',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              <h3 style={{ marginTop: 0 }}>Error Loading Agents</h3>
              <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{error}</p>
              <p style={{ fontSize: '0.9rem', marginTop: '1rem', color: 'var(--text-secondary)' }}>
                Please make sure the backend server is running on port 5001.
                <br />
                <br />
                To start the backend server, run:
                <br />
                <code style={{ 
                  background: 'rgba(0,0,0,0.1)', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                  display: 'inline-block',
                  marginTop: '0.5rem'
                }}>
                  cd backend && JWT_SECRET=premium-homes-secret-key-change-in-production PORT=5001 node server.js
                </code>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agents-page">
      <div className="container">
        <div className="agents-header">
          <h1>{t('agents.title') || 'Agjentët'}</h1>
        </div>

        <div className="agents-layout">
          <div className="agents-main">
            <div className="agents-results">
              {agents.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem',
                  background: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  border: '2px solid var(--border-color)'
                }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', margin: 0 }}>
                    There are no agents
                  </p>
                </div>
              ) : (
                <div className="agents-grid">
                  {agents.map((agent) => (
                  <div
                    key={agent.id}
                    className="agent-card"
                    onClick={() => navigate(`/agent/${agent.id}`, { state: { agent } })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') navigate(`/agent/${agent.id}`, { state: { agent } });
                    }}
                  >
                    <div className="agent-image-container">
                      <AgentPhoto
                        src={agent.image}
                        alt={agent.name}
                        variant="card"
                        className="agent-image"
                      />
                    </div>
                    <div className="agent-info">
                      <h3 className="agent-name">{agent.name}</h3>
                      <p className="agent-title">{agent.title}</p>
                      <div className="agent-details">
                        <p className="agent-properties">
                          {getAgentPropertyCount(allProperties, agent)} {t('agents.properties') || 'Prona'}
                        </p>
                        {agent.mobile && (
                          <a
                            href={getPhoneUrl(agent.mobile)}
                            className="agent-contact-row agent-phone-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="agent-contact-icon">📞</span>
                            <span>{agent.mobile}</span>
                          </a>
                        )}
                        {agent.instagram && (
                          <a
                            href={getInstagramUrl(agent.instagram)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="agent-contact-row agent-instagram-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <InstagramIcon />
                            <span>{formatInstagramDisplay(agent.instagram)}</span>
                          </a>
                        )}
                        {agent.email && (
                          <a
                            href={getEmailUrl(agent.email)}
                            className="agent-contact-row agent-email-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="agent-contact-icon">✉️</span>
                            <span>{agent.email}</span>
                          </a>
                        )}
                      </div>
                      <button
                        className="view-properties-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/agent/${agent.id}`, { state: { agent } });
                        }}
                      >
                        {t('agents.viewProperties') || 'View Properties'}
                      </button>
                    </div>
                  </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="agents-sidebar">
            <div className="sidebar-card sidebar-apply-card">
              <button
                type="button"
                className="apply-agent-btn"
                onClick={() => navigate('/agents/apply')}
              >
                {t('agents.applyAsAgent')}
              </button>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-title">{t('agents.featuredProperties') || 'Featured Properties'}</h3>
              <div className="sidebar-list">
                {featuredProperties.slice(0, 5).map((p) => (
                  <button
                    key={p.id}
                    className="sidebar-property"
                    onClick={() => navigate(`/property/${p.id}`)}
                    type="button"
                  >
                    <div className="sidebar-thumb">
                      {p.images && p.images[0] ? (
                        <img src={resolveMediaUrl(p.images[0])} alt={p.title} />
                      ) : (
                        <div className="sidebar-thumb-fallback">🏠</div>
                      )}
                    </div>
                    <div className="sidebar-property-info">
                      <div className="sidebar-badges">
                        <span className="sidebar-badge">
                          {p.transactionType === 'rent' ? (t('transactionTypes.rent') || 'Rent') : (t('transactionTypes.sale') || 'Sale')}
                        </span>
                        {(() => {
                          const status = resolveListingStatus(p);
                          const statusLabel = getListingStatusLabel(status, t);
                          return statusLabel ? (
                            <span className={`sidebar-badge listing-status-${status}`}>{statusLabel}</span>
                          ) : null;
                        })()}
                      </div>
                      <div className="sidebar-property-title">{p.title}</div>
                      <div className="sidebar-property-meta">{p.location}</div>
                    </div>
                  </button>
                ))}
                {featuredProperties.length === 0 && (
                  <div className="sidebar-empty">{t('agents.noFeatured') || 'No featured properties yet.'}</div>
                )}
              </div>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-title">{t('agents.categories') || 'Categories Property'}</h3>
              <div className="sidebar-categories">
                {categories.map((category) => (
                  <div key={category.type} className="sidebar-category">
                    <span>{category.label}</span>
                    <span className="sidebar-category-count">{category.count}</span>
                  </div>
                ))}
                {categories.length === 0 && (
                  <div className="sidebar-empty">{t('agents.noCategories')}</div>
                )}
              </div>
            </div>

            <div className="sidebar-card">
              <h3 className="sidebar-title">{t('agents.recentlyViewed') || 'Recently Viewed'}</h3>
              <div className="sidebar-list">
                {recentlyViewed.map((p) => (
                  <button
                    key={p.id}
                    className="sidebar-property"
                    onClick={() => navigate(`/property/${p.id}`)}
                    type="button"
                  >
                    <div className="sidebar-thumb">
                      {p.images && p.images[0] ? (
                        <img src={resolveMediaUrl(p.images[0])} alt={p.title} />
                      ) : (
                        <div className="sidebar-thumb-fallback">🏠</div>
                      )}
                    </div>
                    <div className="sidebar-property-info">
                      <div className="sidebar-property-title">{p.title}</div>
                      <div className="sidebar-property-meta">{p.location}</div>
                    </div>
                  </button>
                ))}
                {recentlyViewed.length === 0 && (
                  <div className="sidebar-empty">{t('agents.noRecent') || 'No recently viewed properties yet.'}</div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default AgentsPage;
